/** Macro #30 — Territory social action bar: Like · Save · Comments · Follow · Report. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { cn } from "../cn";
import { socialApi, SocialApiError } from "../../lib/social-api";
import { useSession } from "../marketplace/useSession";
import { FollowButton } from "./FollowButton";
import { ReportMenu } from "./ReportMenu";
import { PILL, PILL_ACTIVE, PILL_CORAL, HeartIcon, BookmarkIcon, CommentIcon } from "./social-ui";
import { useI18n } from "../../i18n";

export function TerritorySocialBar({ identityId }: { identityId: string }) {
  const { t } = useI18n();
  const { isAuthenticated } = useSession();
  const qc = useQueryClient();
  const [err, setErr] = useState<string | null>(null);

  const likesQ = useQuery({
    queryKey: ["social", "likes", identityId],
    queryFn: ({ signal }) => socialApi.territoryLikes(identityId, signal),
    staleTime: 8_000,
    retry: false,
  });

  const savedQ = useQuery({
    queryKey: ["social", "saved-check", identityId],
    queryFn: async ({ signal }) => {
      const page = await socialApi.mySaves(null, signal);
      return page.items.some((i) => i.territory_identity_id === identityId);
    },
    enabled: isAuthenticated,
    staleTime: 8_000,
    retry: false,
  });

  const liked = !!likesQ.data?.liked_by_me;
  const likeCount = likesQ.data?.count ?? 0;
  const saved = !!savedQ.data;

  const likeMut = useMutation({
    mutationFn: () => (liked ? socialApi.unlikeTerritory(identityId) : socialApi.likeTerritory(identityId)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["social", "likes", identityId] }),
    onError: (e) => setErr(e instanceof SocialApiError ? e.message : t("social.bar.likeError")),
  });

  const saveMut = useMutation({
    mutationFn: () => (saved ? socialApi.removeSave(identityId) : socialApi.addSave(identityId)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["social", "saved-check", identityId] });
      qc.invalidateQueries({ queryKey: ["social", "my-saves"] });
    },
    onError: (e) => setErr(e instanceof SocialApiError ? e.message : t("social.bar.saveError")),
  });

  const act = (m: () => void) => {
    setErr(null);
    if (!isAuthenticated) { window.location.hash = "auth"; return; }
    m();
  };

  const scrollToComments = () => {
    document.getElementById("territory-comments")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div data-testid="territory-social-bar" className="flex flex-wrap items-center gap-2.5">
      <button
        type="button"
        data-testid="territory-like-btn"
        data-liked={liked ? "true" : "false"}
        disabled={likeMut.isPending}
        onClick={() => act(() => likeMut.mutate())}
        className={cn(PILL, liked && PILL_CORAL)}
        aria-pressed={liked}
      >
        <HeartIcon filled={liked} />
        <span data-testid="territory-like-count">{likeCount}</span>
      </button>

      <button
        type="button"
        data-testid="territory-save-btn"
        data-saved={saved ? "true" : "false"}
        disabled={saveMut.isPending}
        onClick={() => act(() => saveMut.mutate())}
        className={cn(PILL, saved && PILL_ACTIVE)}
        aria-pressed={saved}
      >
        <BookmarkIcon filled={saved} />
        {saved ? t("social.bar.saved") : t("social.bar.save")}
      </button>

      <button type="button" data-testid="territory-comments-jump" onClick={scrollToComments} className={PILL}>
        <CommentIcon /> {t("social.bar.comments")}
      </button>

      <FollowButton territoryIdentityId={identityId} />

      <ReportMenu targetType="territory" targetId={identityId} testId="territory-report-menu" />

      {err && <span data-testid="territory-social-error" className="w-full font-mono text-[10px] text-state-danger">{err}</span>}
    </div>
  );
}
